import { CommonModule, SlicePipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    signal,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormGroup,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { AppStateService } from '@modules/core/services/app-state.service';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    map,
    Observable,
    skip,
    Subject,
    take,
    takeUntil,
    throttleTime,
} from 'rxjs';
import { TrafficDeviationInformation } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-information';
import { DeviationAffectedComponent } from '@components/traffic-deviations/components/deviation-affected/deviation-affected.component';
import { DeviationInformationComponent } from '@components/traffic-deviations/components/deviation-information/deviation-information.component';
import { FormSelectWithErrorsComponent } from '@modules/shared/components/form-select-with-errors/form-select-with-errors.component';
import { FormSelectOption } from '@components/traffic-deviations/components/form-select/interfaces/form-select-option';
import {
    EffectedRoutesOnStop,
    LineSearchResult,
    StopPlace,
    StopSearchResult,
} from '@components/traffic-deviations/components/searches/components/search-stop/interfaces';
import {
    TrafficDeviationSearchStore,
    TrafficDeviationSearchService,
} from '@components/traffic-deviations/components/searches';
import {
    Call,
    RouteInfo,
    TrafficDeviationForm,
} from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { ArrayMinLengthValidator } from '@components/traffic-deviations/validators/array-min-length-validator/array-min-length.validator';
import { DeviationAffectedTripComponent } from '@components/traffic-deviations/components/deviation-affected-trip/deviation-affected-trip.component';
import { InternalCommentComponent } from '@components/traffic-deviations/components/internal-comment/internal-comment.component';
import { TrafficDeviationTripService } from '@components/traffic-deviations/services/traffic-deviation-trip.service';
import { ConfirmationDialogData } from '@modules/shared/dialogs';
import { DialogService } from '@modules/shared/services/dialog.service';
import { AuthService } from '@modules/core/services';
import { Router } from '@angular/router';
import { TrafficDeviationTypeDeviationInformation } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-deviation-information';
import { TrafficDeviationInformationAdvancedOptions } from '@modules/shared/models/traffic-deviation-information-advanced-options';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { APP_CONTEXT } from '@modules/core/models/app-state';
import { dirtyCheck } from '@modules/core/operators/dirty-check.operator';
import { AffectedTrip } from '@components/traffic-deviations/components/deviation-affected-trip/deviation-affected-trip';
import { TrafficDeviationReceiptStatusFailedComponent } from './components/traffic-deviation-receipt-status-failed/traffic-deviation-receipt-status-failed.component';
import { ValidationSummaryComponent, ValidationError } from '@modules/shared/components/validation-summary/validation-summary.component';
import { FormSwitchComponent } from '@components/traffic-deviations/components/form-switch/form-switch.component';

@Component({
    selector: 'create-new-traffic-deviation-overview',
    imports: [
        CommonModule,
        DeviationInformationComponent,
        TranslateModule,
        MatIconModule,
        MatButtonModule,
        ReactiveFormsModule,
        DeviationAffectedComponent,
        DeviationAffectedTripComponent,
        FormSelectWithErrorsComponent,
        InternalCommentComponent,
        SlicePipe,
        TrafficDeviationReceiptStatusFailedComponent,
        ValidationSummaryComponent,
        FormSwitchComponent,
    ],
    templateUrl: './create-traffic-deviation-overview.component.html',
    styleUrls: ['./create-traffic-deviation-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTrafficDeviationOverviewComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    @Input() set id(id: string) {
        this.existingTrafficDeviationId = id;
        this._deviationService.existingTrafficDeviationLoaded$.next(true);
    }

    @ViewChild('form') form: ElementRef<HTMLDivElement>;
    @ViewChild(DeviationInformationComponent)
    deviationInformationComponent: DeviationInformationComponent;
    @ViewChild(DeviationAffectedComponent)
    deviationAffectedComponent: DeviationAffectedComponent;

    private readonly _formBuilder = inject(NonNullableFormBuilder);
    private readonly _router = inject(Router);
    private readonly _appStateService = inject(AppStateService);
    private readonly _translateService = inject(TranslateService);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _deviationService = inject(TrafficDeviationService);
    private readonly _searchService = inject(TrafficDeviationSearchService);
    private readonly _searchStore = inject(TrafficDeviationSearchStore);
    private readonly _tripService = inject(TrafficDeviationTripService);
    private readonly _dialogService = inject(DialogService);
    private readonly _authService = inject(AuthService);
    private readonly _appState = inject(AppStateService);

    protected existingTrafficDeviationId: string;
    protected formSubmitted = false;
    protected isDirty$: Observable<boolean>;
    protected showValidationSummary = false;
    protected readonly additionalValidationErrors = signal<ValidationError[]>([]);

    protected readonly initialForm$ = new BehaviorSubject({});
    protected readonly formLoading$ = new BehaviorSubject(true);
    protected readonly deviationTypeOptions =
        this._deviationService.deviationTypeOptions;
    protected readonly toggleTrafficDeviationInformation$ =
        this._deviationService.toggleTrafficDeviationInformation$;
    protected readonly trafficDeviationsConfig$ =
        this._deviationService.trafficDeviationsConfig$;
    protected readonly deviationType$ = this._deviationService.deviationType$;
    protected readonly selectedDeviationType$ =
        this._deviationService.selectedDeviationType$;
    protected readonly deviationReasonOptions$ = new BehaviorSubject<
        ReadonlyArray<FormSelectOption>
    >([]);
    protected readonly deviationTypeOptions$ =
        this._deviationService.deviationTypeOptions$;
    protected readonly deviationScope$ = this._deviationService.deviationScope$;
    protected readonly toggleInternalComment$ =
        this._deviationService.toggleInternalComment$;
    protected readonly existingTrafficDeviationLoaded$ =
        this._deviationService.existingTrafficDeviationLoaded$;
    private readonly _destroy$ = new Subject<void>();
    private readonly _timeOutIDs: ReturnType<typeof setTimeout>[] = [];

    protected get trafficDeviationForm() {
        return this._deviationService.trafficDeviationForm;
    }

    private get getterAffected() {
        return this.trafficDeviationForm?.controls.affected;
    }

    private _propagateDeviationReason = true;
    private _initFlag = true;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.id) this._populateFormWithExistingTrafficDeviation();
    }

    ngOnInit(): void {
        this._initForm();
        this._setupSubscriptions();
        this._setDeviationTypeOptions();
        this._setDeviationReasons(
            this._translateService.currentLang.split('_')[0],
        );
        this._handleAccessibility();
        this._deviationService.resetRequiredValidators();
        this.formLoading$.next(this.existingTrafficDeviationId !== undefined);
        this._initFlag = false;
    }

    ngAfterViewInit(): void {
        (<HTMLSelectElement>(
            document.getElementById('deviationTypeSelect')
        ))?.focus();

        this.deviationAffectedComponent?.standaloneStatusChanges$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                if (this.showValidationSummary) {
                    this._updateAdditionalValidationErrors();
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._deviationService.resetStates();
        this._searchService.resetSearchState();
        this._tripService.resetTripState();
        this._deviationService.trafficDeviationForm.reset();
        this._timeOutIDs.forEach((id) => clearTimeout(id));
    }

    private _updateAdditionalValidationErrors(): void {
        const errors: ValidationError[] = [];
        if (this.deviationAffectedComponent?.hasEffectedRoutesErrors()) {
            errors.push({ field: 'noActiveRoutesSelected', fieldLabel: 'Inga påverkade linjer valda', message: '', control: null });
        }
        if (this.deviationAffectedComponent?.hasStopAreaErrors()) {
            errors.push({ field: 'noActiveStopsSelected', fieldLabel: 'No affected stops selected', message: '', control: null });
        }
        if (this.deviationAffectedComponent?.hasStopAreaMismatchErrors()) {
            errors.push({ field: 'stopAreasMismatch', fieldLabel: 'stopAreasMismatch', message: '', control: null });
        }
        this.additionalValidationErrors.set(errors);
    }

    private markFormGroupTouched(control: AbstractControl): void {
        control.markAsTouched();

        if (control instanceof FormGroup) {
            Object.values(control.controls).forEach((child) => {
                this.markFormGroupTouched(child);
            });
        } else if (control instanceof FormArray) {
            control.controls.forEach((child) => {
                this.markFormGroupTouched(child);
            });
        }

        // Also mark replace stops as touched when marking the form as touched
        this.deviationAffectedComponent?.markReplaceStopsAsTouched();
        this.deviationAffectedComponent?.markStopAreasTouched();
    }

    protected closeBlade() {
        this._router.navigate(['', { outlets: { outlet0: null } }]);
    }

    /**
     * Form submit creates the request object and triggers a post/put call to TA.
     */
    protected async onSubmit(): Promise<void> {
        if (this.formSubmitted) return;

        this.markFormGroupTouched(this.trafficDeviationForm);
        this._updateAdditionalValidationErrors();

        const hasExtraErrors = this.additionalValidationErrors().length > 0;

        if (!this.trafficDeviationForm.valid || hasExtraErrors) {
            this.showValidationSummary = true;
            this.formSubmitted = false;
            return;
        }

        this.showValidationSummary = false;
        this.formSubmitted = true;

        const {
            hastusRegion,
            garageCode,
            affected,
            trafficDeviationInformation,
            internalComment,
            ...trafficDeviationScope
        } = this.trafficDeviationForm.getRawValue();

        const startDate = this._deviationService.formatAndOrCombineDateAndTime(
            affected.startDate,
            affected.startTime,
        );
        const endDate = this._deviationService.formatAndOrCombineDateAndTime(
            affected.endDate,
            affected.endTime,
        );

        let request = {
            type: this._trafficDeviationsStore.stateSnapshot
                .selectedDeviationType,
            garageCode: garageCode,
            hastusRegion: hastusRegion,
            trafficDeviationScope: {
                version: trafficDeviationScope.version + 1,
                userFullName: this._authService.user.fullName,
                deviationReason: trafficDeviationScope.deviationReason,
            },
            internalComment: internalComment,
        } as TrafficDeviationTypesUnion;

        let selectedTrip: AffectedTrip;
        this._tripService.selectedTrip$.pipe(take(1)).subscribe((trip) => {
            if (trip) selectedTrip = trip;
        });

        let addTrafficDeviationInformation =
            this.toggleTrafficDeviationInformation$.value;
        const affectedRaw = this.trafficDeviationForm.getRawValue().affected;
        switch (request.type) {
            case 'ChangeOfJourneyTiming':
                request.trafficDeviationScope.routeInfo =
                    affectedRaw.routeInfo.map((r) => ({
                        routeId: r.routeId,
                        routeName: r.routeName,
                        routePublicName: r.routePublicName || r.routeName,
                        trafficDay:
                            this._deviationService.formatAndOrCombineDateAndTime(
                                r.trafficDay,
                            ),
                        tripNumber: r.tripNumber,
                        vehicleTaskNumber: r.vehicleTaskNumber,
                        calls: r.calls.map(({ placeId, name }) => ({
                            placeId,
                            name,
                        })),
                    }));
                request.trafficDeviationScope.startDate = startDate;
                request.trafficDeviationScope.endDate = endDate;
                request.trafficDeviationScope.minutes = affected.minutes;
                request.trafficDeviationScope.affectedTrip = selectedTrip;
                break;
            case 'DeviationInformation':
                addTrafficDeviationInformation = true;
                request.trafficDeviationScope.startDate = startDate;
                request.trafficDeviationScope.endDate = endDate;
                request.trafficDeviationScope.routeInfo =
                    affectedRaw.routeInfo.map((r) => {
                        const routeInfo = {
                            routeId: r.routeId,
                            routeName: r.routeName,
                            routePublicName: r.routePublicName || r.routeName,
                            tripNumber: r.tripNumber ? r.tripNumber : undefined,
                            direction: r.direction ? r.direction : undefined,
                            firstStopDestinationSign: r.firstStopDestinationSign
                                ? r.firstStopDestinationSign
                                : undefined,
                            vehicleTaskNumber: r.vehicleTaskNumber
                                ? r.vehicleTaskNumber
                                : undefined,
                            calls: [],
                            stopCentric: r.stopCentric,
                        };

                        r.calls?.forEach((call) => {
                            if (call?.placeId) {
                                routeInfo.calls.push({
                                    placeId: call.placeId,
                                    name: call.name,
                                    platformCode: call.platformCode,
                                    placeLatitude: call.placeLatitude,
                                    placeLongitude: call.placeLongitude,
                                    effectedRoutes: call.effectedRoutes,
                                    replaceName: undefined,
                                    replacePlaceId: undefined,
                                });
                            }
                        });

                        return routeInfo;
                    });
                request.trafficDeviationScope.affectedTrip = selectedTrip;
                break;
            case 'ChangeOfJourneyPattern':
                request.trafficDeviationScope.startDate = startDate;
                if (endDate) request.trafficDeviationScope.endDate = endDate;
                request.trafficDeviationScope.routeInfo =
                    affectedRaw.routeInfo.map((r) => ({
                        routeId: r.routeId,
                        routeName: r.routeName,
                        routePublicName: r.routePublicName || r.routeName,
                        tripNumber: r.tripNumber ? r.tripNumber : undefined,
                        direction: r.direction ? r.direction : undefined,
                        firstStopDestinationSign: r.firstStopDestinationSign
                            ? r.firstStopDestinationSign
                            : undefined,
                        vehicleTaskNumber: r.vehicleTaskNumber
                            ? r.vehicleTaskNumber
                            : undefined,
                        calls: r.calls.map((call) => {
                            if (call.replacePlaceId === 'retracted') {
                                call.replaceName = undefined;
                                call.replacePlaceId = undefined;
                            }
                            return call;
                        }),
                    }));
                break;
            case 'PredictionInaccurate':
                request.trafficDeviationScope.startDate = startDate;
                request.trafficDeviationScope.endDate = endDate;
                request.trafficDeviationScope.routeInfo =
                    affectedRaw.routeInfo.map((r) => ({
                        routeId: r.routeId,
                        routeName: r.routeName,
                        routePublicName: r.routePublicName || r.routeName,
                        tripNumber: r.tripNumber ? r.tripNumber : undefined,
                        vehicleTaskNumber: r.vehicleTaskNumber
                            ? r.vehicleTaskNumber
                            : undefined,
                        calls: null,
                    }));
                request.trafficDeviationScope.affectedTrip = selectedTrip;
                break;
            case 'ConnectionCancellation':
                addTrafficDeviationInformation = false;
                request.trafficDeviationScope.startDate = startDate;
                request.trafficDeviationScope.endDate = endDate;
                request.trafficDeviationScope.routeInfo =
                    affectedRaw.routeInfo.map((r) => ({
                        routeId: r.routeId,
                        vehicleTaskNumber: r.vehicleTaskNumber,
                        trafficDay:
                            this._deviationService.formatAndOrCombineDateAndTime(
                                r.trafficDay,
                            ),
                        routeName: r.routeName,
                        routePublicName: r.routePublicName || r.routeName,
                        tripNumber: r.tripNumber,
                        calls: r.calls.map(({ placeId, name }) => ({
                            placeId,
                            name,
                        })),
                        transportAuthorityNumber: r.transportAuthorityNumber,
                    }));
                request.trafficDeviationScope.affectedTrip = selectedTrip;
                break;
        }

        if (addTrafficDeviationInformation) {
            this._addTrafficDeviationInformationOnSubmit(
                request,
                trafficDeviationInformation,
            );
        }

        request.trafficDeviationScope.routeInfo?.forEach((r) => {
            if (r.tripNumber && r.direction) {
                r.direction = undefined;
            }
        });

        if (this.existingTrafficDeviationId) {
            request.id = this.existingTrafficDeviationId;
            request.createdAt =
                this._trafficDeviationsStore.stateSnapshot.trafficDeviations.find(
                    (d) => d.id === this.existingTrafficDeviationId,
                )?.createdAt;
            await this._deviationService.putTrafficDeviation(request);
        } else {
            await this._deviationService.postTrafficDeviation(request);
            this._resetForm();
        }

        const routerUrl = this._router.routerState.snapshot.url;
        if (routerUrl.includes('outlet1:selected-line/stop-areas')) {
            this._router.navigate(['', { outlets: { outlet1: null } }]);
        }

        if (
            this._appState.stateSnapshot?.selectedContext !==
            APP_CONTEXT.TRAFFIC_DEVIATIONS
        ) {
            this._appState.changeContext(APP_CONTEXT.TRAFFIC_DEVIATIONS);
        }
        this.formSubmitted = false;
    }

    protected async recallDeviation($event: MouseEvent): Promise<void> {
        $event.stopPropagation();
        const confirm = await this.confirmRecall();
        if (!confirm) {
            return;
        }
        this._deviationService.recallTrafficDeviation(
            this.existingTrafficDeviationId,
        );
        const encodedItemId = encodeURIComponent(
            this.existingTrafficDeviationId,
        );
        const routerUrl = this._router.routerState.snapshot.url;
        const currentItemOpen = routerUrl.indexOf(
            `traffic-deviation/${encodedItemId}`,
        );

        if (currentItemOpen) {
            this._router.navigate(['', { outlets: { outlet0: null } }]);
        }
    }

    protected async confirmRecall(): Promise<boolean> {
        const modalData = new ConfirmationDialogData(
            this._translateService.instant('Warning'),
            this._translateService.instant(
                'Are you sure you want to recall this deviation?',
            ),
            this._translateService.instant('Continue'),
            this._translateService.instant('Cancel'),
        );
        return (
            (await firstValueFrom(
                this._dialogService.showConfirmation(modalData),
            )) !== undefined
        );
    }

    private _setupSubscriptions() {
        // when toggling information block make sure to set correct validation
        this.toggleTrafficDeviationInformation$
            .pipe(takeUntil(this._destroy$))
            .subscribe((toggleTrafficDeviationInformation) => {
                if (toggleTrafficDeviationInformation) {
                    this._deviationService.addDeviationInformationValidation();
                }
                if (!toggleTrafficDeviationInformation) {
                    this._deviationService.removeDeviationInformationValidation();
                    const trafficDeviationInformation =
                        this.trafficDeviationForm.controls
                            .trafficDeviationInformation;

                    trafficDeviationInformation.patchValue({
                        content: '',
                        header: '',
                        summary: '',
                        allChipsHasValue: true,
                        templateId: '',
                        displayScope: {
                            signDisplay: true,
                            journeyPlanner: true,
                            advancedOptions: '',
                        },
                    });

                    const customControl =
                        this.trafficDeviationForm.controls
                            .trafficDeviationInformation.controls.custom;

                    if (customControl.at(0)?.controls) {
                        for (const control of Object.values(
                            customControl.at(0).controls,
                        )) {
                            control.setValue('');
                        }
                    }

                    if (customControl.at(1)) {
                        customControl.removeAt(1);
                    }

                    trafficDeviationInformation.markAsUntouched();
                    trafficDeviationInformation.markAsPristine();
                }
            });

        // when toggling internal comment block make sure to set correct validation
        this.toggleInternalComment$
            .pipe(takeUntil(this._destroy$))
            .subscribe((toggleInternalComment) => {
                if (!toggleInternalComment) {
                    this.trafficDeviationForm?.controls.internalComment.reset();
                }
                this._deviationService.resetRequiredValidators();
                // Emit statusChanges so the validation summary refreshes immediately.
                // resetRequiredValidators uses emitEvent: false internally, so we
                // need one explicit emit after validators have been reconfigured.
                this.trafficDeviationForm?.controls.internalComment.updateValueAndValidity();
            });

        // on language change make sure the reasons are translated and sorted
        this._translateService.onLangChange
            .pipe(takeUntil(this._destroy$))
            .subscribe(({ lang }) => {
                const activeReason = this.trafficDeviationForm?.controls.deviationReason?.value;
                this._setDeviationReasons(lang.split('_')[0], activeReason);
            });

        // if the deviation type is changed make sure to run _deviationTypeSelect which sets
        // the selected value and makes sure to _resetFormArrays and _resetRequiredValidators.
        this.deviationTypeOptions$
            .pipe(
                skip(1),
                filter(() => {
                    // made it possible to filter out specific emited value if
                    // _propagateDeviationReason is false.
                    if (!this._propagateDeviationReason) {
                        this._propagateDeviationReason = true;
                        return false;
                    }
                    return true;
                }),
                map(
                    (deviationTypeOptions) =>
                        deviationTypeOptions?.filter(
                            ({ selected }) => selected,
                        )[0],
                ),
                takeUntil(this._destroy$),
            )
            .subscribe((deviationTypeOption) => {
                if (!deviationTypeOption) return;
                this._deviationTypeSelect(
                    deviationTypeOption.value as TrafficDeviationTypesUnion['type'],
                );
            });

        // adds choosenStops and choosenLines to form
        combineLatest([
            this._searchService.choosenStops$.pipe(distinctUntilChanged()),
            this._searchService.choosenLines$.pipe(distinctUntilChanged()),
        ])
            .pipe(skip(1), takeUntil(this._destroy$))
            .subscribe(([choosenStops, choosenLines]) => {
                const tripNumbers = this.getterAffected.value.routeInfo.map(
                    (line) => line.tripNumber,
                );

                this.getterAffected.controls.routeInfo.clear();
                if (!choosenStops.length && !choosenLines.length) return;

                this._addChoosenStopsToRouteInfo(choosenStops);
                this._addChoosenLinesToRouteInfo(choosenLines);

                // make sure tripNumber is populated again after routeInfo.clear()
                this.getterAffected.controls.routeInfo.controls.forEach(
                    (routeInfo, index) => {
                        if (tripNumbers[index]) {
                        routeInfo.patchValue({
                                tripNumber: tripNumbers[index],
                        });
                }
                    },
                );

                this._deviationService.resetRequiredValidators();
                this.getterAffected.controls.routeInfo.updateValueAndValidity();
            });

        this._searchStore
            .getSelectedHastusRegion()
            .pipe(skip(1), distinctUntilChanged(), takeUntil(this._destroy$))
            .subscribe((hastusRegion) => {
                this._appStateService
                    .getSelectedGaragesState()
                    .pipe(
                        take(1),
                        map(
                            (garages) =>
                                garages.filter(
                                    (g) => g.hastusRegionCode === hastusRegion,
                                )[0],
                        ),
                    )
                    .subscribe((garage) => {
                        const garageCode =
                            this.trafficDeviationForm.controls.garageCode.value;
                        const sameHastusRegion =
                            garage?.hastusRegionCode === hastusRegion;

                        if (garageCode && sameHastusRegion) return;
                        this.trafficDeviationForm.patchValue({
                            hastusRegion: hastusRegion,
                            garageCode: garage?.code ?? '',
                        });
                    });
            });

        this._tripService.selectedTrip$
            .pipe(
                skip(1),
                distinctUntilChanged(),
                takeUntil(this._destroy$),
            )
            .subscribe((trip) => {
                if (!trip) return;

                this.trafficDeviationForm.patchValue({
                    hastusRegion: trip.hastusRegion,
                    garageCode: trip.garageCode,
                });
            });

        this.trafficDeviationForm.controls.affected.controls.routeInfo.valueChanges
            .pipe(
                throttleTime(200, null, { leading: false, trailing: true }),
                takeUntil(this._destroy$),
            )
            .subscribe((routeInfo) => {
                const isStopCentric = routeInfo?.some((r) => r.stopCentric);
                const form = this.trafficDeviationForm;
                const trafficDeviationInformation =
                    form.controls.trafficDeviationInformation;
                const stopScope =
                    trafficDeviationInformation.controls.stopScope;

                if (stopScope.value === 'LineCentric' && isStopCentric) {
                    stopScope.setValue('StopCentric');
                } else if (
                    stopScope.value === 'StopCentric' &&
                    !isStopCentric
                ) {
                    stopScope.setValue('LineCentric');
                }
            });
    }

    private _addChoosenStopsToRouteInfo(
        choosenStops: StopSearchResult[],
    ): void {
        const emptyRoute = { stopPlaces: [] } as EffectedRoutesOnStop & {
            stopPlaces: StopPlace[];
        };
        const uniqueRoutes: Array<
            EffectedRoutesOnStop & { stopPlaces: StopPlace[] }
        > = [];
        const stopCentric = choosenStops?.some(
            (s) => s.type === 'StopArea' && s.stopCentric,
        );

        choosenStops?.forEach((stop) => {
            if (stop.type === 'StopArea') {
                stop.stopPlaces?.forEach((sp) => {
                    this._getUniqueRoutesAndIncludedStopPoints(
                        sp,
                        uniqueRoutes,
                    );
                });
                return;
            }

            this._getUniqueRoutesAndIncludedStopPoints(stop, uniqueRoutes);
        });

        if (!uniqueRoutes.length) {
            choosenStops.forEach((stop) => {
                if (stop.type === 'StopArea') {
                    stop.stopPlaces.forEach((stopPlace) => {
                        if (!stopPlace.active) return;
                        const index = emptyRoute.stopPlaces.findIndex(
                            (s) => s.placeId === stopPlace.placeId,
                        );
                        if (index === -1) {
                            if (
                                !emptyRoute.routeId &&
                                stop.effectedRoutes?.length
                            ) {
                                const route = stop.effectedRoutes[0];
                                emptyRoute.routeId = route.routeId;
                                emptyRoute.routeName = route.routeName;
                                emptyRoute.routePublicName =
                                    route.routePublicName;
                                emptyRoute.firstStopDestinationSign =
                                    route.firstStopDestinationSign;
                                emptyRoute.direction = route.direction;
                            }
                            emptyRoute.stopPlaces.push(stopPlace);
                        }
                    });
                }
            });
            if (emptyRoute.stopPlaces.length) uniqueRoutes.push(emptyRoute);
        }

        uniqueRoutes.forEach((route) => {
            const calls: FormGroup<TypedForm<Call>>[] = [];
            route.stopPlaces.forEach((s) => {
                calls.push(
                    this._deviationService.addCall({
                        name: s.description,
                        platformCode: s.platformCode,
                        placeId: s.placeId,
                        parentId: s.parentId,
                        placeLatitude: s.placeLatitude,
                        placeLongitude: s.placeLongitude,
                        replaceName: s.replaceName,
                        replacePlaceId: s.replacePlaceId,
                        effectedRoutes: s.effectedRoutes.filter((r) => {
                            return (
                                r.active &&
                                r.routeName === route.routeName &&
                                r.direction === route.direction
                            );
                        }),
                    }),
                );
            });

            const routeInfo = this._deviationService.addRouteInfo(
                { ...route, stopCentric },
                calls,
            );
            this.getterAffected.controls.routeInfo.push(routeInfo);
        });
    }

    private _addChoosenLinesToRouteInfo(
        choosenLines: LineSearchResult[],
    ): void {
        for (const line of choosenLines) {
            const calls: FormGroup<TypedForm<Call>>[] = [];
            if (line.stopPlaces?.length) {
                for (const stopArea of line.stopPlaces) {
                    const stopAreas = stopArea.stopPlaces.filter(
                        (s) => s.selected,
                    );
                    for (const stopPlace of stopAreas) {
                        const index = calls.findIndex(
                            (c) => c.value.placeId === stopPlace.placeId,
                        );
                        if (index === -1) {
                            calls.push(
                                this._deviationService.addCall({
                                    name: stopPlace.placeDescription,
                                    platformCode: stopPlace.platformCode,
                                    placeId: stopPlace.placeId,
                                    parentId: stopPlace.parentId,
                                    placeLatitude: stopPlace.placeLatitude,
                                    placeLongitude: stopPlace.placeLongitude,
                                    effectedRoutes: stopPlace.effectedRoutes,
                                }),
                            );
                        }
                    }
                }
            }

            const routeInfo = this._deviationService.addRouteInfo(line, calls);
            this.getterAffected.controls.routeInfo.push(routeInfo);
        }
    }

    private _getUniqueRoutesAndIncludedStopPoints(
        stop: StopPlace,
        uniqueRoutes: Array<EffectedRoutesOnStop & { stopPlaces: StopPlace[] }>,
    ): void {
        if (!stop.active) return;

        stop.effectedRoutes?.forEach((r) => {
            if (!r.active) return;

            const routeExists = uniqueRoutes.find((u) => {
                return (
                    u.routeName === r.routeName && u.direction === r.direction
                );
            });
            if (routeExists) {
                const stopExists = routeExists.stopPlaces.find(
                    (s) => s.placeId === stop.placeId,
                );
                if (!stopExists) routeExists.stopPlaces.push(stop);
            }
            if (!routeExists) uniqueRoutes.push({ ...r, stopPlaces: [stop] });
        });
    }

    /**
     * Adds accessibility to component
     */
    private _handleAccessibility(): void {
        this._timeOutIDs.push(
            setTimeout(() => {
                this.form.nativeElement.addEventListener(
                    'keydown',
                    (event: KeyboardEvent) => {
                        if (event.key === 'F2') {
                            event.stopPropagation();
                            this._searchService.focusSearchField$.next();
                        }
                    },
                );
            }, 100),
        );
    }

    /**
     * populates the select for deviation type and handles the change in selected value,
     * @param propagateDeviationReason sets _propagateDeviationReason which controls
     * if the changed value should be emited.
     */
    private _setDeviationTypeOptions(propagateDeviationReason = true) {
        this._propagateDeviationReason = propagateDeviationReason;
        this._deviationService.initializeDeviationTypeOptions(this._destroy$);
    }

    /**
     * Sets traffic deviation type and resets form and validators
     * @param selectedType traffic deviation type
     * @param skipDefaultEmptyRows default false
     */
    private _deviationTypeSelect(
        selectedDeviationType: TrafficDeviationTypesUnion['type'],
        skipDefaultEmptyRows = false,
    ): void {
        this._trafficDeviationsStore.updateTrafficDeviationState({
            selectedDeviationType,
        });
        this._resetFormArrays(skipDefaultEmptyRows);

        this.getterAffected.controls.routeInfo.controls.forEach((routeInfo) => {
            this._deviationService.removeValidators(routeInfo.controls.calls, [
                ArrayMinLengthValidator(2),
            ]);
        });

        this._deviationService.resetRequiredValidators();
    }

    /**
     * Adds data regarding traffic deviation information to the request object.
     * @param request: TrafficDeviationTypesUnion
     * @param trafficDeviationInformation: TrafficDeviationForm['trafficDeviationInformation']
     */
    private _addTrafficDeviationInformationOnSubmit(
        request: TrafficDeviationTypesUnion,
        trafficDeviationInformation: TrafficDeviationForm['trafficDeviationInformation'],
    ): void {
        const summary =
            this.deviationInformationComponent.getTextareaWithChips('summary');
        const content =
            this.deviationInformationComponent.getTextareaWithChips('content');
        const header =
            this.deviationInformationComponent.getTextareaWithChips('header');

        request.trafficDeviationScope.trafficDeviationInformation = {
            templateId: trafficDeviationInformation.templateId,
            version: trafficDeviationInformation.version,
            summary: summary,
            content: content,
            header: header,
            displayScope: {},
            stopScope: trafficDeviationInformation.stopScope,
            publicationEndDate: '',
            publicationStartDate: '',
            custom: trafficDeviationInformation.custom,
        };

        const trafficDeviationScope = request.trafficDeviationScope;
        const displayScope =
            trafficDeviationScope.trafficDeviationInformation.displayScope;

        if (trafficDeviationInformation.displayScope.signDisplay) {
            displayScope.signDisplay = {};
            if (
                trafficDeviationInformation.displayScope.advancedOptions ==
                    TrafficDeviationInformationAdvancedOptions.EXCLUSIVE ||
                trafficDeviationInformation.displayScope.advancedOptions ==
                    TrafficDeviationInformationAdvancedOptions.BOARD_ONLY
            ) {
                displayScope.signDisplay.advancedOptions =
                    trafficDeviationInformation.displayScope.advancedOptions;
            }
        }
        if (trafficDeviationInformation.displayScope.journeyPlanner) {
            displayScope.journeyPlanner = {};
            if (
                trafficDeviationInformation.displayScope.advancedOptions ==
                TrafficDeviationInformationAdvancedOptions.STOP_INDEPENDENT
            ) {
                displayScope.journeyPlanner.advancedOptions =
                    trafficDeviationInformation.displayScope.advancedOptions;
            }
        }

        const requesttrafficDeviationInformation =
            request.trafficDeviationScope.trafficDeviationInformation;
        requesttrafficDeviationInformation.publicationStartDate =
            this._deviationService.formatAndOrCombineDateAndTime(
                trafficDeviationInformation.publicationStartDate,
                trafficDeviationInformation.publicationStartTime,
            );
        requesttrafficDeviationInformation.publicationEndDate =
            this._deviationService.formatAndOrCombineDateAndTime(
                trafficDeviationInformation.publicationEndDate,
                trafficDeviationInformation.publicationEndTime,
            );

        if ('startDate' in request.trafficDeviationScope) {
            requesttrafficDeviationInformation.publicationStartDate =
                requesttrafficDeviationInformation.publicationStartDate
                    ? requesttrafficDeviationInformation.publicationStartDate
                    : request.trafficDeviationScope.startDate;
            requesttrafficDeviationInformation.publicationEndDate =
                requesttrafficDeviationInformation.publicationEndDate
                    ? requesttrafficDeviationInformation.publicationEndDate
                    : request.trafficDeviationScope.endDate;
        }

        this._handlePublicationEndDateTime(request);
    }

    private _handlePublicationEndDateTime(
        request: TrafficDeviationTypesUnion,
    ): void {
        this._trafficDeviationsStore
            .getTrafficDeviationOnId(this.existingTrafficDeviationId)
            .pipe(take(1))
            .subscribe((trafficDeviation) => {
                if (!trafficDeviation) return;
                const endDate = request.trafficDeviationScope.endDate;
                const publicationEndDate =
                    request.trafficDeviationScope.trafficDeviationInformation
                        .publicationEndDate;

                if (endDate === trafficDeviation.trafficDeviationScope.endDate)
                    return;

                if (publicationEndDate && endDate) {
                    if (publicationEndDate < endDate) {
                        request.trafficDeviationScope.trafficDeviationInformation.publicationEndDate =
                            endDate;
                    }
                }
            });
    }

    /**
     * When opening existing traffic deviation this method populates the form with that data.
     * @returns Promise<void>
     */
    private async _populateFormWithExistingTrafficDeviation(): Promise<void> {
        this.toggleTrafficDeviationInformation$.next(false);
        this.formLoading$.next(true);
        // fetches traffic deviation to open but also insures that the
        // form is initialized before we populate the form.
        const trafficDeviation = await firstValueFrom(
            this._trafficDeviationsStore.getTrafficDeviationOnId(
                this.existingTrafficDeviationId,
            ),
        );
        if (!trafficDeviation) return;

        this._deviationTypeSelect(trafficDeviation.type, true);
        this.trafficDeviationForm.reset({}, { emitEvent: false });
        this._setDeviationTypeOptions(false);
        this._setDeviationReasons(
            this._translateService.currentLang.split('_')[0],
            trafficDeviation.trafficDeviationScope.deviationReason,
        );

        const { routeInfo, ...deviationScope } =
            trafficDeviation.trafficDeviationScope;
        const startDate = this._deviationService.formatDateString(
            deviationScope.startDate,
        );
        const endDate = this._deviationService.formatDateString(
            deviationScope.endDate,
        );
        const startTime = this._deviationService.splitTimeFromDate(
            deviationScope.startDate,
        );
        const endTime = this._deviationService.splitTimeFromDate(
            deviationScope.endDate,
        );

        let trafficDeviationInformation: TrafficDeviationForm['trafficDeviationInformation'];
        const informationFromMessage =
            trafficDeviation.trafficDeviationScope.trafficDeviationInformation;
        if (informationFromMessage) {
            trafficDeviationInformation = this._addTrafficDeviationInformation(
                informationFromMessage,
            );
        }

        this.trafficDeviationForm.patchValue({
            ...trafficDeviation,
            ...deviationScope,
            affected: {
                ...deviationScope,
                startDate: startDate,
                startTime: startTime,
                endDate: endDate,
                endTime: endTime,
            },
            trafficDeviationInformation: trafficDeviationInformation,
        });

        this.getterAffected.controls.routeInfo.clear();
        routeInfo?.forEach((routeInfo: RouteInfo) => {
            const calls: FormGroup<TypedForm<Call>>[] = [];
            routeInfo.calls?.forEach((call) => {
                calls.push(
                    this._deviationService.addCall({
                        name: call.name,
                        placeId: call.placeId,
                        parentId: call.parentId,
                        placeLatitude: call.placeLatitude,
                        placeLongitude: call.placeLongitude,
                        replaceName: call.replaceName,
                        replacePlaceId: call.replacePlaceId,
                    }),
                );
            });

            routeInfo.stopCentric =
                informationFromMessage?.stopScope === 'StopCentric';
            this.getterAffected.controls.routeInfo.push(
                this._deviationService.addRouteInfo(routeInfo, calls),
            );
        });

        const customControl =
            this.trafficDeviationForm.controls.trafficDeviationInformation
                .controls.custom;
        customControl.clear();

        let customs: Array<Record<string, string | number | boolean>> = [];
        // this code handles backup compatibility where custom property was an object and not an array
        if (
            informationFromMessage?.custom &&
            !Array.isArray(informationFromMessage.custom)
        ) {
            customs = [
                {
                    Time: '',
                    Departures: '',
                    'Date and Time': '',
                    CustomFieldDescription: '',
                    'Amount of Minutes': '',
                },
            ];
        } else if (
            informationFromMessage?.custom &&
            Array.isArray(informationFromMessage.custom)
        ) {
            customs = informationFromMessage.custom;
        } else {
            customs = [
                {
                    Time: '',
                    Departures: '',
                    'Date and Time': '',
                    CustomFieldDescription: '',
                    'Amount of Minutes': '',
                },
            ];
        }

        customs?.forEach((custom) => {
            customControl.push(this._formBuilder.group(custom));
        });

        if (
            trafficDeviation.type === 'ChangeOfJourneyPattern' ||
            trafficDeviation.type === 'DeviationInformation'
        ) {
            this._searchService.convertRouteInfoToSelectedLinesAndStops(
                structuredClone(
                    trafficDeviation.trafficDeviationScope.routeInfo,
                ) as RouteInfo[],
                trafficDeviation.hastusRegion,
            );

            if (trafficDeviation.type === 'DeviationInformation') {
                const choosenLines: LineSearchResult[] = [];
                const deviationInformationRouteInfo =
                    routeInfo as TrafficDeviationTypeDeviationInformation['trafficDeviationScope']['routeInfo'];

                for (const route of deviationInformationRouteInfo.filter(
                    (info) => !info.calls?.length,
                )) {
                    choosenLines.push({
                        routeId: route.routeId,
                        direction: route.direction,
                        firstStopDestinationSign:
                            route.firstStopDestinationSign,
                        hastusRegion: trafficDeviation.hastusRegion,
                        routeName: route.routeName,
                        routePublicName: route.routePublicName,
                        searchType: 'LineSearch',
                    });
                }

                this._searchService.choosenLines$.next(choosenLines);
            }
        }

        this.toggleTrafficDeviationInformation$.next(!!informationFromMessage);
        this.toggleInternalComment$.next(!!trafficDeviation.internalComment);
        this._deviationService.resetRequiredValidators();

        if (!this._initFlag && this.deviationInformationComponent) {
            this.deviationInformationComponent.reInitialize();
        }

        this.trafficDeviationForm.markAllAsTouched();

        this._timeOutIDs.push(
            setTimeout(() => {
                this.initialForm$.next(
                    structuredClone(this.trafficDeviationForm.value),
                );
                if (!this.isDirty$) {
                    this.isDirty$ = this.trafficDeviationForm.valueChanges.pipe(
                        dirtyCheck(this.initialForm$.asObservable()),
                        takeUntil(this._destroy$),
                    );
                }
            }, 100),
        );

        // added this because the isDirty$ check flicker between true/false when changing from
        // one traffic deviation to another.
        this._timeOutIDs.push(
            setTimeout(() => {
                this.formLoading$.next(false);
            }, 1000),
        );
    }

    /**
     * Resets all FormArrays in the form.
     * @param skipDefaultEmptyRows skips the logic where we add rows to routeInfo and calls
     */
    private _resetFormArrays(skipDefaultEmptyRows = false): void {
        this.getterAffected.controls.routeInfo.clear();

        if (skipDefaultEmptyRows) return;
        this.getterAffected.controls.routeInfo.setControl(
            0,
            this._formBuilder.group({
                routeName: ['', [Validators.maxLength(50)]],
                routeId: ['', [Validators.maxLength(50)]],
                routePublicName: ['', [Validators.maxLength(50)]],
                tripNumber: [
                    '',
                    [Validators.maxLength(50), Validators.pattern('^[0-9]+$')],
                ],
                vehicleTaskNumber: ['', [Validators.maxLength(50)]],
                direction: [undefined],
                firstStopDestinationSign: ['', [Validators.maxLength(50)]],
                trafficDay: ['', [Validators.maxLength(50)]],
                calls: this._formBuilder.array([
                    this._formBuilder.group({
                        placeId: ['', [Validators.maxLength(50)]],
                        parentId: ['', [Validators.maxLength(50)]],
                        name: ['', [Validators.maxLength(50)]],
                        platformCode: ['', [Validators.maxLength(50)]],
                        placeLatitude: [undefined],
                        placeLongitude: [undefined],
                        replacePlaceId: ['', [Validators.maxLength(50)]],
                        replaceName: ['', [Validators.maxLength(50)]],
                        effectedRoutes: this._formBuilder.array([
                            this._formBuilder.group({
                                firstStopDestinationSign: [
                                    '',
                                    [Validators.maxLength(50)],
                                ],
                                routeName: ['', [Validators.maxLength(50)]],
                            }),
                        ]),
                    }),
                ]),
                transportAuthorityNumber: [''],
                stopCentric: [false],
            }),
        );
    }

    /**
     * Reset form and restore form to its initial state.
     */
    private _resetForm(): void {
        const startTime = new Date();
        const endTime = new Date();
        startTime.setSeconds(0);
        endTime.setMinutes(endTime.getMinutes() + 30);
        endTime.setSeconds(0);

        this._resetFormArrays();
        this.trafficDeviationForm.reset({
            affected: {
                startDate: startTime.toLocaleDateString('sv'),
                startTime: startTime.toLocaleTimeString('sv'),
                endDate: endTime.toLocaleDateString('sv'),
                endTime: endTime.toLocaleTimeString('sv'),
            },
        });
        this._deviationService.resetRequiredValidators();
        this.deviationInformationComponent?.togglePublicationPeriod.setValue(
            false,
        );
        this.toggleInternalComment$.next(false);
        this._tripService.resetTripState();
        this._deviationService.resetStates();
        this._searchService.resetSearchState();
    }

    /**
     * Adds traffic deviation information to the form.
     * @param trafficDeviationInformation: FormGroup<TypedForm<TrafficDeviationForm['trafficDeviationInformation']
     * @returns: TrafficDeviationForm['trafficDeviationInformation']
     */
    private _addTrafficDeviationInformation({
        displayScope,
        custom,
        ...deviationInformation
    }: TrafficDeviationInformation): TrafficDeviationForm['trafficDeviationInformation'] {
        const formDisplayScope = {
            journeyPlanner: !!displayScope.journeyPlanner,
            signDisplay: !!displayScope.signDisplay,
            advancedOptions:
                displayScope.journeyPlanner?.advancedOptions ??
                displayScope.signDisplay?.advancedOptions,
        };

        const pubStartDate = this._deviationService.formatDateString(
            deviationInformation.publicationStartDate,
        );
        const pubEndDate = this._deviationService.formatDateString(
            deviationInformation.publicationEndDate,
        );
        const pubStartTime = this._deviationService.splitTimeFromDate(
            deviationInformation.publicationStartDate,
        );
        const pubEndTime = this._deviationService.splitTimeFromDate(
            deviationInformation.publicationEndDate,
        );

        return {
            ...deviationInformation,
            allChipsHasValue: true,
            displayScope: formDisplayScope,
            publicationStartDate: pubStartDate,
            publicationEndDate: pubEndDate,
            publicationStartTime: pubStartTime,
            publicationEndTime: pubEndTime,
            custom: [],
        };
    }

    /**
     * Sets all deviation reasons in select.
     * Handles translation and sorting.
     * @param currentLang: string
     */
    private _setDeviationReasons(currentLang: string, activeReason?: string): void {
        const deviationReasons: FormSelectOption[] = [
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'ACCIDENT',
                text: this._translateService.instant('ACCIDENT'),
            },
            {
                showFor: [],
                value: 'ASSAULT',
                text: this._translateService.instant('ASSAULT'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'CHANGEDROUTE',
                text: this._translateService.instant('CHANGEDROUTE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'CONGESTION',
                text: this._translateService.instant('CONGESTION'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'DELAYS',
                text: this._translateService.instant('DELAYS'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'EARLYDEPARTURE',
                text: this._translateService.instant('EARLYDEPARTURE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'HEAVYLOAD',
                text: this._translateService.instant('HEAVYLOAD'),
            },
            {
                showFor: [],
                value: 'INFRASTRUCTUREFAILURE',
                text: this._translateService.instant('INFRASTRUCTUREFAILURE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'MAINTENANCE',
                text: this._translateService.instant('MAINTENANCE'),
            },
            {
                showFor: [],
                value: 'MARKETINGINFO',
                text: this._translateService.instant('MARKETINGINFO'),
            },
            {
                showFor: [],
                value: 'MEDICAL',
                text: this._translateService.instant('MEDICAL'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'NODRIVER',
                text: this._translateService.instant('NODRIVER'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'OTHER',
                text: this._translateService.instant('OTHER'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'POLICEDECISION',
                text: this._translateService.instant('POLICEDECISION'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'REDUCEDSPEED',
                text: this._translateService.instant('REDUCEDSPEED'),
            },
            {
                showFor: [],
                value: 'RESTOREOPERATION',
                text: this._translateService.instant('RESTOREOPERATION'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'ROADWORK',
                text: this._translateService.instant('ROADWORK'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'SAFETY',
                text: this._translateService.instant('SAFETY'),
            },
            {
                showFor: [],
                value: 'SERVICEINFO',
                text: this._translateService.instant('SERVICEINFO'),
            },
            {
                showFor: [],
                value: 'SMOKE',
                text: this._translateService.instant('SMOKE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'STRIKE',
                text: this._translateService.instant('STRIKE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'TECHNICALFAILURE',
                text: this._translateService.instant('TECHNICALFAILURE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'TRAFFICINCIDENT',
                text: this._translateService.instant('TRAFFICINCIDENT'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'VANDALISM',
                text: this._translateService.instant('VANDALISM'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'VEHICLEBREAKDOWN',
                text: this._translateService.instant('VEHICLEBREAKDOWN'),
            },
            {
                showFor: [
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'VEHICLESHORTAGE',
                text: this._translateService.instant('VEHICLESHORTAGE'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'WEATHER',
                text: this._translateService.instant('WEATHER'),
            },
            {
                showFor: [
                    'ChangeOfJourneyPattern',
                    'ChangeOfJourneyTiming',
                    'PredictionInaccurate',
                    'DeviationInformation',
                    'ConnectionCancellation',
                ],
                value: 'WRONGROUTE',
                text: this._translateService.instant('WRONGROUTE'),
            },
        ];

        const visibleReasons = [...deviationReasons]
            .sort((a, b) => a.text.localeCompare(b.text, currentLang))
            .filter(r => r.showFor.length > 0 || r.value === activeReason)
            .map(r => r.showFor.length === 0 ? { ...r, disabled: true } : r);

        visibleReasons.unshift({
            value: '',
            text: this._translateService.instant('Choose cause'),
            showFor: [
                'ChangeOfJourneyPattern',
                'ChangeOfJourneyTiming',
                'PredictionInaccurate',
                'DeviationInformation',
                'ConnectionCancellation',
            ],
            selected: true,
        });
        this.deviationReasonOptions$.next(visibleReasons);
    }

    private _initForm() {
        this._deviationService.createForm();

        this._deviationService.existingTrafficDeviationLoaded$
            .pipe(take(1))
            .subscribe((existingTrafficDeviationLoaded) => {
                if (existingTrafficDeviationLoaded) {
                    const affected =
                        this._deviationService.trafficDeviationForm.controls
                            .affected.controls;
                    affected.minutes.removeValidators([
                        Validators.pattern('^-[1-9][0-9]*$|^[1-9][0-9]*$'),
                    ]);
                }
            });
    }

    private addInvalidControl(controls, invalid): void {
        for (const name in controls) {
            const childControl = controls[name];
            if (childControl.errors) {
                if (childControl.parent instanceof FormArray) {
                    invalid[`Array[${name}]`] = childControl.errors;
                } else {
                    invalid[name] = childControl.errors;
                }
            }
            if (
                childControl instanceof FormGroup ||
                childControl instanceof FormArray
            ) {
                this.addInvalidControl(childControl.controls, invalid);
            }
        }
    }
}
